/*
 * The Clerk-gated boot chrome every product app shows before its own UI exists:
 * the sign-in gate, the not-an-admin wall, and the fatal-error screen.
 *
 * Every app had written all of this itself. signInAppearance and fatal were
 * byte-identical in three of them; mountNotAuthorized differed by one phrase
 * naming the surface; mountSignIn differed by one optional background. So it was
 * never three implementations — it was one, copied, with the copies free to
 * drift apart on the first screen a user ever sees.
 *
 * These take `root` explicitly rather than reaching for a module-level element,
 * because each app owns its own mount point.
 */
import { SPLASH_GRADIENT } from './branding';
import { getResolvedTheme } from './theme';
/**
 * Clerk appearance matching the current app theme.
 *
 * `darkTheme` is Clerk's own dark baseTheme, passed in rather than imported:
 * base-ui takes no Clerk dependency, so the app supplies it.
 *
 * rootBox is stretched and centred because Clerk's own card sizing leaves it
 * pinned to the top-left inside a grid parent.
 */
export function signInAppearance(darkTheme) {
    return {
        baseTheme: getResolvedTheme() === 'dark' ? darkTheme : undefined,
        elements: {
            rootBox: {
                position: 'relative',
                width: '100%',
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            },
        },
    };
}
/**
 * The key that records "this person just signed out".
 *
 * sessionStorage, not local: it must die with the tab, so a fresh visit tomorrow
 * still auto-redirects. Exported so the sign-out path can set it without
 * duplicating the literal — two spellings of this key means the flag is written
 * and never read, and the bug that returns is the unsignoutable tab.
 */
export const JUST_SIGNED_OUT = 'vitesse.signedOut';
/** Records that the user deliberately signed out. Call BEFORE the IdP call. */
export function markSignedOut() {
    try {
        sessionStorage.setItem(JUST_SIGNED_OUT, '1');
    }
    catch {
        // Private mode / storage disabled. The cost is one extra click on the gate,
        // never a broken sign-out, because the gate still renders its button.
    }
}
/**
 * The sign-in gate.
 *
 * `position: fixed; inset: 0` rather than an in-flow `min-height`, which let the
 * card drift off-centre.
 *
 * The brand fade is the DEFAULT, not an option each app passes. It was optional
 * once, and the result was the splash handing over to a bare white page on the
 * two surfaces whose boot code did not opt in — the same product, arriving
 * differently depending on which door you came through. An app that genuinely
 * wants something else still passes `background`; an app that says nothing gets
 * the brand.
 */
export function mountSignIn(root, host, opts = {}) {
    /*
     * MOUNT ONCE PER SIGNED-OUT SPELL.
     *
     * Callers drive this from the identity provider's change listener, which fires
     * on ANY client change — including the ones the sign-in form itself causes as
     * the person fills it in. Re-entering here tore the card down and built a new
     * one, and a freshly built card opens a NEW sign-in attempt against the
     * provider. Typing an email address was enough to spend several, and the
     * provider answered 429; the screen then showed "Too many requests" to someone
     * who had not yet requested anything.
     *
     * The theme is part of the key because a theme switch is the one legitimate
     * reason to rebuild while still signed out.
     */
    const key = opts.darkTheme ? 'dark' : 'light';
    const live = root.querySelector(':scope > [data-vitesse-signin]');
    if (live?.getAttribute('data-vitesse-signin') === key)
        return;
    root.replaceChildren();
    const gate = document.createElement('div');
    // The marker rides the GATE, not the root, so it disappears the moment the app
    // replaces these children on sign-in. Nothing has to remember to clear a flag,
    // and a later sign-out mounts a fresh card because the marker is genuinely gone.
    gate.setAttribute('data-vitesse-signin', key);
    gate.style.cssText = 'position:fixed;inset:0;display:grid;place-items:center;';
    gate.style.background = opts.background ?? SPLASH_GRADIENT;
    root.appendChild(gate);
    /*
     * A CARD IF THE PROVIDER HAS ONE, a redirect if it does not.
     *
     * Clerk mounts its own sign-in card here, which is the whole reason this gate
     * is a full-viewport centring container. A provider that ships no embeddable
     * card (a hosted-page IdP) is served by the branch below instead, so the boot
     * chrome does not have to be rewritten the next time that changes.
     */
    if (host.mountSignIn) {
        const mount = host.mountSignIn;
        mount.call(host, gate, { appearance: signInAppearance(opts.darkTheme) });
        return;
    }
    /*
     * NO CARD: redirect, except straight after a sign-out.
     *
     * Arriving at an app you have access to should not cost a click on a page whose
     * only purpose is to say "continue". So the gate navigates on its own.
     *
     * The one case that must NOT auto-redirect is the moment after signing out:
     * that user lands here too, and bouncing them back to the IdP makes signing out
     * impossible without closing the tab. `markSignedOut()` sets a one-shot flag,
     * and this reads-and-clears it — one-shot, so a later reload of the same tab
     * behaves normally again.
     *
     * The card is still built either way. It is what the person looks at while the
     * navigation happens (instead of a white screen if the IdP is slow), and it is
     * the fallback when the redirect cannot run at all.
     */
    let deliberate = false;
    try {
        deliberate = sessionStorage.getItem(JUST_SIGNED_OUT) === '1';
        if (deliberate)
            sessionStorage.removeItem(JUST_SIGNED_OUT);
    }
    catch {
        // Storage unavailable — fall through to auto-redirect. Erring toward the
        // common path (arriving) over the rare one (having just signed out).
    }
    const card = document.createElement('div');
    card.style.cssText = 'text-align:center;color:var(--text-primary);font-family:var(--font)';
    const btn = document.createElement('button');
    btn.textContent = 'Sign in';
    btn.className = 'base-button base-button-primary';
    btn.addEventListener('click', () => {
        if (!host.signIn)
            return;
        btn.disabled = true; // a second click restarts the navigation and loses the first
        void Promise.resolve(host.signIn()).catch((e) => {
            btn.disabled = false;
            fatal(root, 'Sign-in unavailable', e);
        });
    });
    card.appendChild(btn);
    gate.appendChild(card);
    if (!host.signIn) {
        fatal(root, 'Sign-in unavailable', new Error('identity host offers neither mountSignIn nor signIn'));
        return;
    }
    if (!deliberate)
        btn.click();
}
/**
 * The wall for a signed-in user who is not an admin of the active org.
 *
 * `surface` names the thing they cannot reach ("the fleet admin", "the AI
 * console") so the message says which door is shut.
 */
/**
 * The NO-ORGANIZATION gate: a signed-in person who belongs to no organization yet.
 *
 * WHY IT EXISTS. Without it the identity provider takes over: the session carries
 * an unfinished task, and the provider redirects to its own hosted account portal
 * to resolve it. That works, and it is the wrong experience — the person is
 * bounced off the product onto a differently-branded domain, completes a step
 * there, and is sent back. The first thing a new customer sees should not be
 * somebody else's website.
 *
 * So the SAME component is mounted here instead, inside the app, on the same
 * brand fade the sign-in card sits on. Nothing about the flow changes; only where
 * it happens.
 *
 * Mount-once for the same reason mountSignIn is: callers drive this from the
 * provider's change listener, which fires on any client change, and rebuilding
 * the component underneath someone mid-typing loses what they typed.
 */
export function mountOrgGate(root, host, opts = {}) {
    if (!host.mountOrganizationList)
        return; // provider has no embeddable chooser
    const key = opts.darkTheme ? 'dark' : 'light';
    const live = root.querySelector(':scope > [data-vitesse-orggate]');
    if (live?.getAttribute('data-vitesse-orggate') === key)
        return;
    root.replaceChildren();
    const gate = document.createElement('div');
    gate.setAttribute('data-vitesse-orggate', key);
    gate.style.cssText = 'position:fixed;inset:0;display:grid;place-items:center;';
    gate.style.background = opts.background ?? SPLASH_GRADIENT;
    root.appendChild(gate);
    const mount = host.mountOrganizationList;
    mount.call(host, gate, {
        appearance: signInAppearance(opts.darkTheme),
        // Both halves on one screen: a person invited to an existing org and a person
        // starting a new one arrive here identically, and asking which they are before
        // showing them anything is a question they cannot answer.
        hidePersonal: true,
    });
}
export function mountNotAuthorized(root, host, surface) {
    root.replaceChildren();
    const wrap = document.createElement('div');
    wrap.style.cssText =
        'min-height:100vh;display:grid;place-items:center;padding:2rem;text-align:center;background:var(--bg-primary);color:var(--text-primary)';
    const card = document.createElement('div');
    card.style.cssText = 'max-width:32rem';
    card.innerHTML =
        `<p style="font-weight:600;font-size:1.1rem;margin:0 0 .5rem">Admin access required</p>` +
            `<p style="color:var(--text-secondary);font-size:.9rem;line-height:1.5;margin:0 0 1.25rem">` +
            `${surface} is restricted to organization admins. Switch to an organization where ` +
            `you're an admin, or ask your org admin for access.</p>`;
    const out = document.createElement('button');
    out.textContent = 'Sign out';
    // The shared button class, not inline chrome — this renders in the light DOM,
    // so base-ui's stylesheet reaches it.
    out.className = 'base-button base-button-neutral';
    out.addEventListener('click', () => {
        markSignedOut();
        void host.signOut();
    });
    card.appendChild(out);
    wrap.appendChild(card);
    root.appendChild(wrap);
}
/**
 * The last-resort screen when boot itself fails.
 *
 * Deliberately plain innerHTML with no component dependency: this has to render
 * when the reason for calling it might be that the app failed to initialise.
 */
export function fatal(root, title, err) {
    const detail = err instanceof Error ? err.message : String(err);
    root.replaceChildren();
    root.innerHTML =
        `<div style="min-height:100vh;display:grid;place-items:center;padding:2rem;text-align:center">` +
            `<div><p style="color:var(--red);font-weight:600;margin:0 0 .5rem">${title}</p>` +
            `<p style="color:var(--text-secondary);font-size:.875rem;max-width:40rem;margin:0">${detail}</p>` +
            `</div></div>`;
    console.error(`[boot] ${title}:`, err);
}
//# sourceMappingURL=boot.js.map